import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useConversation } from '@11labs/react';

interface AgentTestModalProps {
  agentId: string;
  agentName: string;
  onClose: () => void;
}

const AgentTestModal: React.FC<AgentTestModalProps> = ({ agentId, agentName, onClose }) => {
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [messages, setMessages] = useState<Array<{ text: string; sender: 'user' | 'agent'; ts: number }>>([]);
  const [error, setError] = useState<string | null>(null);
  const chatRef = useRef<HTMLDivElement>(null);

  // Auto scroll with smooth behavior
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTo({
        top: chatRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages]);

  const conversation = useConversation({
    onConnect: () => {
      setError(null);
      setIsConnecting(false);
    },
    onDisconnect: () => {
      setIsConnecting(false);
    },
    onMessage: (msg) => {
      if (msg.message && msg.source) {
        setMessages((prev) => [...prev, { text: msg.message, sender: msg.source === 'ai' ? 'agent' : 'user', ts: Date.now() }]);
      }
    },
    onError: (err) => {
      console.error(err);
      const msg = typeof err === 'string' ? err : (err as any)?.message;
      setError(msg || 'Conversation error');
      setIsConnecting(false);
    },
  });

  const requestMic = async () => {
    setIsRequestingPermission(true);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      setIsPermissionGranted(true);
    } catch (e) {
      setError('Microphone permission denied');
    } finally {
      setIsRequestingPermission(false);
    }
  };

  const startConversation = async () => {
    setIsConnecting(true);
    setError(null);
    try {
      await conversation.startSession({ agentId });
    } catch (e: any) {
      console.error(e);
      setError(e.message || 'Failed to start session');
      setIsConnecting(false);
    }
  };

  const endConversation = async () => {
    await conversation.endSession();
  };

  const connected = conversation.status === 'connected';
  const speaking = conversation.isSpeaking;

  return createPortal(
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="relative bg-gray-900 w-full max-w-4xl rounded-2xl shadow-lg overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 bg-gray-900/60 border-b border-white/10">
          <h3 className="text-2xl font-bold text-white">Test Agent: {agentName}</h3>
          <button onClick={onClose} className="text-gray-300 hover:text-white text-3xl">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-900" ref={chatRef}>
          {error && (
            <div className="p-4 bg-red-900/50 rounded-lg border border-red-500 text-red-300">
              {error}
            </div>
          )}
          {isConnecting && (
            <div className="text-center text-gray-300">Connecting to agent...</div>
          )}
          {messages.length === 0 && !isConnecting && (
            <div className="text-center text-gray-400 py-8">
              Start the conversation to see messages here
            </div>
          )}
          {messages.map((m) => (
            <div key={m.ts} className={`flex ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-md px-4 py-3 rounded-2xl ${
                m.sender === 'user' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-800 text-white'
              }`}>
                {m.text}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-900/50 border-t border-white/10">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            {!isPermissionGranted ? (
              <button
                onClick={requestMic}
                disabled={isRequestingPermission}
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center justify-center gap-2"
              >
                {isRequestingPermission ? 'Requesting...' : 'Enable Microphone'}
              </button>
            ) : connected ? (
              <button
                onClick={endConversation}
                className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg"
              >
                End Conversation
              </button>
            ) : (
              <button
                onClick={startConversation}
                disabled={isConnecting}
                className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg"
              >
                {isConnecting ? 'Connecting...' : 'Start Conversation'}
              </button>
            )}
            {connected && (
              <span className="text-gray-300">
                {speaking ? 'Agent speaking...' : 'Listening...'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AgentTestModal;

